/* eslint-disable react/prop-types */
/* eslint-disable no-case-declarations */
import { useState, useEffect } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Chip,
  IconButton,
  Skeleton,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Sort,
  Assignment,
  PriorityHigh,
  CheckCircle,
} from "@mui/icons-material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import useGetDetailledObservation from "@/hooks/useGetDetailledObservation";
import ObservationDetailsDialog from "@pages/user/observations/observationDetailsDialog";
import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import { useTranslation } from "react-i18next";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
  ...(theme.palette.mode === "dark" && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.4)} 100%)`,
  }),
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.8125rem",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    cursor: "pointer",
  },
  "& .MuiTableCell-root": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
    padding: theme.spacing(2),
    fontSize: "0.8125rem",
  },
}));

const UserObservationsTable = ({ userId, userName, onError }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Pagination state
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [initialTotalCount, setInitialTotalCount] = useState(null);
  const currentPageToken = pageTokens[currentPageIndex];

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Fetch observations
  const queryUrl = currentPageToken
    ? `/api/v1/users/${userId}/observations?cursor=${currentPageToken}`
    : `/api/v1/users/${userId}/observations`;
  const { data, isLoading, error } = useAuthenticatedQuery(
    ["userObservations", userId, currentPageToken],
    queryUrl
  );

  const { getDetailledData } = useGetDetailledObservation();
  const [selectedObservation, setSelectedObservation] = useState(null);

  // Process data and handle pagination
  useEffect(() => {
    if (!data?.records) return;

    // For first page (no cursor), we should replace existing data
    if (currentPageToken == null) {
      // This is the first page of a new query, reset everything
      setPages([data.records]);
      setCurrentPageIndex(0);
      setInitialTotalCount(data.total_count || 0);
    } else {
      // For subsequent pages, add to pages array
      setPages((prev) => {
        const newPages = [...prev];
        newPages[currentPageIndex] = data.records;
        return newPages;
      });
    }

    // Add next page token if available
    if (data.next_page && !pageTokens.includes(data.next_page)) {
      setPageTokens((prev) => [...prev, data.next_page]);
    }
  }, [data, currentPageIndex, currentPageToken, pageTokens]);

  const fetchObservationDetails = async (id) => {
    try {
      const detail = await getDetailledData(id);
      setSelectedObservation(detail);
    } catch (err) {
      onError(err);
    }
  };

  const handleLoadMore = () => {
    if (data?.next_page) {
      setCurrentPageIndex((prev) => prev + 1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("complete") || statusLower.includes("closed")) {
      return "success";
    } else if (
      statusLower.includes("pending") ||
      statusLower.includes("open")
    ) {
      return "warning";
    } else if (statusLower.includes("review")) {
      return "info";
    }
    return "default";
  };

  const sortRecords = (records) => {
    if (!sortConfig.key) return records;

    const sorted = [...records];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "type":
          aValue = a.workflow?.name || t("userObservationsTable.unknownText");
          bValue = b.workflow?.name || t("userObservationsTable.unknownText");
          break;
        case "priority":
          aValue =
            a.observation_priority?.name ||
            t("userObservationsTable.unknownText");
          bValue =
            b.observation_priority?.name ||
            t("userObservationsTable.unknownText");
          break;
        case "status":
          const aSteps = a.history_steps || [];
          const bSteps = b.history_steps || [];
          aValue = aSteps.length
            ? aSteps[aSteps.length - 1].status
            : t("userObservationsTable.unknownStatus");
          bValue = bSteps.length
            ? bSteps[bSteps.length - 1].status
            : t("userObservationsTable.unknownStatus");
          break;
        case "created_at":
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case "description":
          aValue = a.description || t("userObservationsTable.unknownText");
          bValue = b.description || t("userObservationsTable.unknownText");
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Table Header Skeleton */}
        <StyledTableContainer component={Paper}>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                {[
                  "Type",
                  "Priority",
                  "Status",
                  "Created At",
                  "Description",
                ].map((header, index) => (
                  <TableCell key={index}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="circular" width={16} height={16} />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {[...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton variant="text" width="80%" height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={24}
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="70%" height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="90%" height={20} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Footer Skeleton */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Skeleton variant="text" width={200} height={24} />
          <Skeleton
            variant="rectangular"
            width={100}
            height={32}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    onError(error);
    return null;
  }

  // Aggregate all loaded pages
  const records = pages.slice(0, currentPageIndex + 1).flat();
  const sortedRecords = sortRecords(records);
  const nextPage = data?.next_page;

  if (sortedRecords.length === 0) {
    return (
      <Paper
        sx={{
          p: 6,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.5)} 0%, ${alpha(theme.palette.grey[100], 0.2)} 100%)`,
          ...(theme.palette.mode === "dark" && {
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.5)} 0%, ${alpha(theme.palette.grey[900], 0.2)} 100%)`,
          }),
        }}
      >
        <Assignment
          sx={{
            fontSize: 64,
            color: "text.disabled",
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            mb: 1,
          }}
        >
          No Observations Found
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.disabled",
          }}
        >
          {t("userObservationsTable.noObservationsText", { userName })}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box aria-label={t("userObservationsTable.tableAria")}>
      <StyledTableContainer component={Paper}>
        <Table size="small" aria-label={t("userObservationsTable.tableAria")}>
          <StyledTableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Assignment sx={{ fontSize: 16, color: "text.secondary" }} />
                  {t("userObservationsTable.typeHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("type")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      width: 24,
                      height: 24,
                    }}
                    aria-label={t("userObservationsTable.sortTypeAria")}
                  >
                    {sortConfig.key === "type" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      ) : (
                        <ArrowDownward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      )
                    ) : (
                      <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PriorityHigh
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                  {t("userObservationsTable.priorityHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("priority")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      width: 24,
                      height: 24,
                    }}
                    aria-label={t("userObservationsTable.sortPriorityAria")}
                  >
                    {sortConfig.key === "priority" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      ) : (
                        <ArrowDownward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      )
                    ) : (
                      <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircle sx={{ fontSize: 16, color: "text.secondary" }} />
                  {t("userObservationsTable.statusHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("status")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      width: 24,
                      height: 24,
                    }}
                    aria-label={t("userObservationsTable.sortStatusAria")}
                  >
                    {sortConfig.key === "status" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      ) : (
                        <ArrowDownward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      )
                    ) : (
                      <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {t("userObservationsTable.createdAtHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("created_at")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      width: 24,
                      height: 24,
                    }}
                    aria-label={t("userObservationsTable.sortCreatedAtAria")}
                  >
                    {sortConfig.key === "created_at" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      ) : (
                        <ArrowDownward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      )
                    ) : (
                      <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {t("userObservationsTable.descriptionHeader")}
                  <IconButton
                    size="small"
                    onClick={() => handleSort("description")}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      width: 24,
                      height: 24,
                    }}
                    aria-label={t("userObservationsTable.sortDescriptionAria")}
                  >
                    {sortConfig.key === "description" ? (
                      sortConfig.direction === "asc" ? (
                        <ArrowUpward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      ) : (
                        <ArrowDownward
                          sx={{ fontSize: 14, color: "primary.main" }}
                        />
                      )
                    ) : (
                      <Sort sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {sortedRecords.map((obs) => {
              const steps = obs.history_steps || [];
              const status = steps.length
                ? steps[steps.length - 1].status
                : t("userObservationsTable.unknownStatus");
              const statusColor = getStatusColor(status);

              return (
                <StyledTableRow
                  key={obs.id}
                  onClick={() => fetchObservationDetails(obs.id)}
                  aria-label={t("userObservationsTable.rowAria", {
                    type:
                      obs.workflow?.name ||
                      t("userObservationsTable.unknownText"),
                  })}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "text.primary" }}
                    >
                      {obs.workflow?.name ||
                        t("userObservationsTable.unknownText")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {obs.observation_priority?.name ||
                        t("userObservationsTable.unknownText")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.toUpperCase()}
                      color={statusColor}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 24,
                      }}
                      aria-label={t("userObservationsTable.statusChipAria", {
                        status,
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {convertUTCToLocal(obs.created_at) ||
                        t("userObservationsTable.unknownText")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.4,
                      }}
                    >
                      {obs.description
                        ? obs.description.length > 50
                          ? `${obs.description.slice(0, 50)}…`
                          : obs.description
                        : t("userObservationsTable.unknownText")}
                    </Typography>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {selectedObservation && (
        <ObservationDetailsDialog
          observation={selectedObservation}
          onClose={() => setSelectedObservation(null)}
        />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          ...(theme.palette.mode === "dark" && {
            bgcolor: alpha(theme.palette.grey[800], 0.5),
          }),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          {t("userObservationsTable.totalCountText", {
            userName,
            count: initialTotalCount || data?.total_count || 0,
          })}
        </Typography>
        {nextPage && (
          <Button
            size="small"
            variant="contained"
            onClick={handleLoadMore}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: theme.shadows[1],
              "&:hover": {
                boxShadow: theme.shadows[2],
              },
            }}
            aria-label={t("userObservationsTable.loadMoreAria")}
          >
            {t("userObservationsTable.loadMoreButton")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UserObservationsTable;
